import React from "react";
import { twMerge } from "tailwind-merge";
import { selectionClass, tabSizeClass } from "./tabStyles";

const COLORS = {
  accent: {
    solid: "bg-ink/10 text-ink border border-ink/15",
    ghost: "text-ink",
  },
  amber: {
    solid: "badge-pastel badge-leaders",
    ghost: "text-ink/70",
  },
  leaders: {
    solid: "badge-pastel badge-leaders",
    ghost: "text-ink/70",
  },
  pink: {
    solid: "badge-pastel badge-welcome",
    ghost: "text-ink/70",
  },
  purple: {
    solid: "badge-pastel badge-leaders",
    ghost: "text-ink/70",
  },
  sky: {
    solid: "badge-pastel bg-followers text-plum",
    ghost: "text-ink/70",
  },
  followers: {
    solid: "badge-pastel bg-followers text-plum",
    ghost: "text-ink/70",
  },
  white: {
    solid: "bg-white/80 text-ink border border-black/[0.06]",
    ghost: "text-ink",
  },
  ink: {
    solid: "bg-ink/10 text-ink border border-ink/15",
    ghost: "text-ink/80",
  },
  cream: {
    solid: "bg-cream text-ink border border-black/[0.06]",
    ghost: "text-ink/70",
  },
  instagram: {
    solid:
      "badge-pastel bg-gradient-to-tr from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white",
    ghost: "text-[#E1306C]",
  },
};

/**
 * Quiet badge / chip — no tilt, no sticker lift.
 * Pastel fills reserved for dance-role semantics.
 */
export default function Badge({
  children,
  color = "ink",
  ghost = false,
  className = "",
  /** @deprecated ignored — tilt retired */
  tilt: _tilt = 0,
  ...rest
}) {
  const palette = COLORS[color] || COLORS.ink;
  return (
    <span
      className={twMerge(
        "inline-flex items-center gap-1 font-ui font-medium text-[12px]",
        ghost ? "bg-transparent" : "px-2.5 py-1 rounded-btn",
        ghost ? palette.ghost : palette.solid,
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/** Count badge for pending actions */
export function CountBadge({ count, className = "" }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className={twMerge(
        "count-badge-warm min-w-[1.25rem] h-5 px-1.5 rounded-btn bg-orchid/40 text-plum text-[11px] font-medium inline-flex items-center justify-center",
        className,
      )}
    >
      {count}
    </span>
  );
}

/** Toggle pill — compact size. Default uses theme selectionClass; role pastels only when color is set. */
export function PillToggle({
  active,
  children,
  onClick,
  color = null,
  className = "",
  ...rest
}) {
  const roleActive = {
    pink: "bg-pinky/70 text-plum border-black/[0.06] font-medium",
    purple: "bg-leaders text-plum border-black/[0.06] font-medium",
    leaders: "bg-leaders text-plum border-black/[0.06] font-medium",
    sky: "bg-followers text-plum border-black/[0.06] font-medium",
    followers: "bg-followers text-plum border-black/[0.06] font-medium",
    cream: "bg-cream text-plum border-black/[0.06] font-medium",
  }[color];

  const stateClass = roleActive
    ? active
      ? roleActive
      : "bg-surface text-ink border-subtle font-medium hover:bg-surface-muted"
    : selectionClass(active);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={twMerge(tabSizeClass("compact"), stateClass, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

